import { createSelector } from 'reselect';
import get from 'lodash/get';
import find from 'lodash/find';
import remove from 'lodash/remove';

import { initialState } from './reducer';
import { createSelectFieldOptions } from '../../utils/common';

const selectAppDomain = state => state.App || initialState;

const selectUserData = () => createSelector(selectAppDomain, subState => get(subState, 'userData'));

const selectUserProfiles = () => createSelector(selectAppDomain, subState => get(subState, 'profiles'));
const selectIsUserProfilesLoading = () =>
  createSelector(selectAppDomain, subState => get(subState, 'isProfilesLoading'));
const selectProfilePermissions = () => createSelector(selectAppDomain, subState => get(subState, 'profilePermissions'));
const selectUserPermissions = () => createSelector(selectAppDomain, subState => get(subState, 'userPermissions'));

const selectActiveProfile = () => createSelector(selectAppDomain, subState => get(subState, 'activeProfileData'));
const selecIsSwitchingProfile = () => createSelector(selectAppDomain, subState => get(subState, 'isSwitchingProfile'));
const selectActiveProfileId = () => createSelector(selectAppDomain, subState => get(subState, 'activeProfileData.id'));
const selectLoginErrorMessage = () => createSelector(selectAppDomain, subState => get(subState, 'loginError'));
const selectLoginLoadingState = () => createSelector(selectAppDomain, subState => get(subState, 'loginLoading'));
const selectLoginAttemptsCount = () => createSelector(selectAppDomain, subState => get(subState, 'loginTries'));

const selecActiveProfileById = profileId =>
  createSelector(selectAppDomain, subState => {
    const profiles = get(subState, 'profiles');
    return find(profiles, { id: Number(profileId) });
  });

const selectArtistProfessionsOptions = (withoutSinger = true) =>
  createSelector(selectAppDomain, subState => {
    const professions = get(subState, 'artistProfessions', []);
    if (withoutSinger) {
      remove(professions, { id: 1 }); // remove 'singer' as we should set voice type instead of it
    }
    return createSelectFieldOptions(professions, 'id', 'name', { type: 'profession' });
  });

const selectAtistProfessions = () =>
  createSelector(selectAppDomain, subState => get(subState, 'artistProfessions', []));

const selectAggregationsValues = key =>
  createSelector(selectAppDomain, subState => get(subState, `aggregations.${key}`, []));

const selectAggregationsOptions = (key, valueName = 'key', labelName = 'name', additionalProps = true) =>
  createSelector(selectAppDomain, subState => {
    const aggregationsList = get(subState, `aggregations.${key}`, []);
    return createSelectFieldOptions(aggregationsList, valueName, labelName, { type: key }, additionalProps);
  });

const selectAggregationsLoading = () =>
  createSelector(selectAppDomain, subState => get(subState, 'aggregationTypeLoading'));

const selectAggregationsError = () => createSelector(selectAppDomain, subState => get(subState, 'aggregations.error'));

const selectStartAggregation = () => createSelector(selectAppDomain, subState => get(subState, 'startAggregations'));

const selectAgenciesListOptions = (withAllData = false) =>
  createSelector(selectAppDomain, subState => {
    const agencies = get(subState, 'agencies', []);
    return createSelectFieldOptions(agencies, 'id', 'name', {}, withAllData);
  });

const selectAgenciesListOptionsLoading = () =>
  createSelector(selectAppDomain, subState => get(subState, 'agenciesLoading'));

const selectCompaniesListOptions = (withAllData = false) =>
  createSelector(selectAppDomain, subState => {
    const companies = get(subState, 'companies', []);
    return createSelectFieldOptions(companies, 'id', 'name', {}, withAllData);
  });

const selectCompaniesListOptionsLoading = () =>
  createSelector(selectAppDomain, subState => get(subState, 'companiesLoading'));

const selectProfilesListOptions = () =>
  createSelector(selectAppDomain, subState => {
    const profiles = get(subState, 'profilesList', []);
    return profiles?.map(profile => {
      const profession = get(profile, 'profession.qualification.name') || get(profile, 'profession.name');
      return {
        ...profile,
        value: get(profile, 'id'),
        label: `${get(profile, 'name')} ${profession ? `(${profession})` : ''}`,
      };
    });
  });

const selectProfilesListLoading = () =>
  createSelector(selectAppDomain, subState => get(subState, 'profilesListLoading'));

const selectResetPasswordMessage = () =>
  createSelector(selectAppDomain, subState => get(subState, 'resetPasswordInfo'));

const selectPasswordKeyStatus = () =>
  createSelector(selectAppDomain, subState => get(subState, 'isResetPasswordKeyActive'));

const selectPasswordUpdateStatus = () =>
  createSelector(selectAppDomain, subState => get(subState, 'isPasswordUpdated'));

const selectNotifications = () => createSelector(selectAppDomain, subState => get(subState, 'notifications', []));

const selectVoiceTypesOptions = () =>
  createSelector(selectAppDomain, subState =>
    createSelectFieldOptions(get(subState, 'voiceTypes.data', []), 'id', 'name'),
  );

const selectCitiesOptions = () =>
  createSelector(selectAppDomain, subState => ({
    options: createSelectFieldOptions(get(subState, 'cities.data', []), 'id', 'name'),
    loading: get(subState, 'cities.loading', false),
  }));

const selectVenuesOptions = () =>
  createSelector(selectAppDomain, subState => ({
    loading: get(subState, 'venues.loading', false),
    options: createSelectFieldOptions(get(subState, 'venues.data', []), 'id', 'name', {}, true),
  }));

const selectStages = () =>
  createSelector(selectAppDomain, subState => ({
    loading: get(subState, 'stages.loading', false),
    data: get(subState, 'stages.data', []),
  }));

const selectBufferValueData = () =>
  createSelector(selectAppDomain, subState => ({
    loading: get(subState, 'bufferValue.loading', false),
    entity: get(subState, 'bufferValue.entity', []),
  }));

const selectWorkTypesOptions = () =>
  createSelector(selectAppDomain, subState =>
    createSelectFieldOptions(get(subState, 'workTypes.data', []), 'id', 'name'),
  );

const selectWorkStylesOptions = () =>
  createSelector(selectAppDomain, subState =>
    createSelectFieldOptions(get(subState, 'workStyles.data', []), 'id', 'name'),
  );

const selectBanner = () => createSelector(selectAppDomain, subState => get(subState, 'notificationBanner'));

const selectPrivacyOptions = () =>
  createSelector(selectAppDomain, subState =>
    createSelectFieldOptions(get(subState, 'privacyOptions.data', []), 'id', 'name'),
  );

const selectPrivacyAttributes = () =>
  createSelector(selectAppDomain, subState => get(subState, 'privacyOptions.data', []));

const selectAOSProfessionsOptions = () =>
  createSelector(selectAppDomain, subState => {
    const professions = get(subState, 'aosProfessions.data', []);
    return createSelectFieldOptions(professions, 'id', 'name', {});
  });

const selectLanguages = () => createSelector(selectAppDomain, subState => get(subState, 'languages.data', false));

const selectLanguagesOptions = () =>
  createSelector(selectLanguages(), langs => createSelectFieldOptions(langs, 'id', 'name', {}, true));

const selectDynamicCountriesOptions = () =>
  createSelector(selectAppDomain, subState =>
    createSelectFieldOptions(get(subState, `${initialState.dynamicCountries.listName}.data`), 'id', 'name'),
  );

export {
  selectAppDomain,
  selectUserData,
  selectUserProfiles,
  selectActiveProfile,
  selectActiveProfileId,
  selecActiveProfileById,
  selectArtistProfessionsOptions,
  selectVoiceTypesOptions,
  selectAtistProfessions,
  selectAggregationsValues,
  selectAggregationsOptions,
  selectAgenciesListOptions,
  selectAgenciesListOptionsLoading,
  selectCompaniesListOptions,
  selectCompaniesListOptionsLoading,
  selectProfilesListOptions,
  selectResetPasswordMessage,
  selectPasswordKeyStatus,
  selectPasswordUpdateStatus,
  selectAggregationsLoading,
  selectProfilesListLoading,
  selectNotifications,
  selectLoginErrorMessage,
  selectStartAggregation,
  selectUserPermissions,
  selectProfilePermissions,
  selectAggregationsError,
  selectIsUserProfilesLoading,
  selecIsSwitchingProfile,
  selectCitiesOptions,
  selectVenuesOptions,
  selectStages,
  selectBufferValueData,
  selectWorkTypesOptions,
  selectWorkStylesOptions,
  selectBanner,
  selectLoginLoadingState,
  selectPrivacyOptions,
  selectPrivacyAttributes,
  selectAOSProfessionsOptions,
  selectLoginAttemptsCount,
  selectLanguages,
  selectLanguagesOptions,
  selectDynamicCountriesOptions,
};
